import { Done, FileCopyOutlined, Info as InfoIcon } from "@mui/icons-material";
import React, { useEffect, useState } from "react";
import { ExternalLink } from "../../../common/components/ExternalLink";
import { NetworkRequest } from "../../../common/models";
import { OrganizationCookieDto } from "../../../common/service/server/dto/CookieScanDto";
import {
  EmploymentGroupNotices,
  PublicOrganizationNoticeDto,
  PublicPrivacyCenterDto,
} from "../../../common/service/server/dto/PublicOrganizationNoticeDto";
import { OrganizationPublicId } from "../../../common/service/server/Types";
import { DeveloperCookieTable } from "../../components/organisms/cookies/DeveloperCookieTable";
import { SurveyGateCollapse } from "../../components/organisms/survey/SurveyGateCollapse";
import { Para } from "../../components/typography/Para";
import { Paras } from "../../components/typography/Paras";
import { useOrganizationVendors, usePrimaryOrganization } from "../../hooks";
import { usePrivacyCenter } from "../../hooks/usePrivacyCenter";
import {
  usePrimaryOrganizationId,
  usePublicOrganizationNotices,
} from "../../hooks/useOrganization";
import { frontendPathToUrl, RouteHelpers, Routes } from "../../root/routes";
import { PrivacyCenterUrls } from "../../util/privacyCenterUtil";
import { PrivacyNoticeActions } from "../../components/organisms/privacy-notice/Foo";
import { OptOutIconSelector } from "../../components/organisms/OptOutIconSelector";
import { Link } from "react-router-dom";
import { Callout, CalloutVariant } from "../../components/Callout";
import { SurveyAnswerFn, SurveyAnswers, useSurvey } from "../../../common/hooks/api/useSurvey";
import {
  useWebsiteAudit,
  WebsiteAudit,
} from "../../components/organisms/website-audit/WebsiteAudit";
import { doesGppSignalApplyForVendor } from "../data-map/vendor-documentation/VendorDocumentationPage";
import { OrganizationDataRecipientDto } from "../../../common/service/server/dto/OrganizationDataRecipientDto";
import { AlertCard, SimpleCheckboxControlled } from "@truevault/aquilo";
import { RefreshFn } from "../../../common/hooks/api";

type UsePrivacyNoticeChecklistItems = {
  checklistProps: PrivacyNoticeChecklistItemsProps;
  ready: boolean;
};

export const usePrivacyNoticeChecklistItems = (
  orgId: OrganizationPublicId,
): UsePrivacyNoticeChecklistItems => {
  const [ready, setReady] = useState(false);

  // network fetches
  const [notices, noticesRequest] = usePublicOrganizationNotices(orgId);

  // Effects
  useEffect(() => {
    if (NetworkRequest.areFinished(noticesRequest)) {
      setReady(true);
    }
  }, [noticesRequest]);

  return {
    checklistProps: { notices },
    ready,
  };
};

export type PrivacyNoticeChecklistItemsProps = {
  notices: PublicOrganizationNoticeDto;
  instructions?: boolean;
  visible?: PublishNoticesTabs[];
  stayCompliant?: boolean;
  isHr?: boolean;
  isPublicView?: boolean;
};

export type PublishNoticesTabs = "web" | "cookie" | "mobile" | "in-store" | "employment";
export const DEVELOPER_INSTRUCTIONS_SURVEY_NAME = "developer-instruction-survey";

export const DevInstructionsSurveySlug = "finished-dev-instructions";

export const DevInstructionsInStoreNoticeSlug = "finished-in-store-instructions";
export const DevInstructionsWebNoticeSlug = "finished-web-instructions";
export const DevInstructionsCookieNoticeSlug = "finished-cookie-instructions";
export const DevInstructionsMobileNoticeSlug = "finished-mobile-instructions";
export const DevInstructionsHrNoticeSlug = "finished-hr-instructions";

export const DevInstructionsConsentManagementFinishedVendorIdsSlug =
  "finished-consent-management-vendor-ids";

const isSurveyAnswerTrue = (answers: SurveyAnswers, slug: string) =>
  answers && !!answers[slug] && answers[slug] === "true";

const filterNonGppVendorsForConsentManagement = (allVendors: OrganizationDataRecipientDto[]) => {
  // In Consent Management section, show vendors that are either missing GPP signal or have marked GPP signal as not respected/unknown
  return (allVendors || [])
    .filter((v) => doesGppSignalApplyForVendor(v))
    .filter(
      (v) => !v.gppSignalStatus || v.gppSignalStatus === "GPP_SIGNAL_NOT_RESPECTED_OR_UNKNOWN",
    );
};

export const getDevInstructionsCompletionStatus = (
  devSurveyAnswers: SurveyAnswers,
  notices: PublicOrganizationNoticeDto,
  allVendors: OrganizationDataRecipientDto[],
): {
  didFinishInstoreInstructions: boolean;
  didFinishWebInstructions: boolean;
  didFinishCookieInstructions: boolean;
  didFinishConsentManagementInstructions: boolean;
  didFinishMobileInstructions: boolean;
  allDevInstructionsNoticesFinished: boolean;
} => {
  if (!devSurveyAnswers || !notices) {
    return {
      didFinishInstoreInstructions: false,
      didFinishWebInstructions: false,
      didFinishCookieInstructions: false,
      didFinishConsentManagementInstructions: false,
      didFinishMobileInstructions: false,
      allDevInstructionsNoticesFinished: false,
    };
  }

  const { showCookieNotice, hasPhysicalLocation, hasMobileApplication } = notices;

  const didFinishInstoreInstructions =
    !hasPhysicalLocation || isSurveyAnswerTrue(devSurveyAnswers, DevInstructionsInStoreNoticeSlug);
  const didFinishWebInstructions = isSurveyAnswerTrue(
    devSurveyAnswers,
    DevInstructionsWebNoticeSlug,
  );
  const didFinishCookieInstructions =
    !showCookieNotice || isSurveyAnswerTrue(devSurveyAnswers, DevInstructionsCookieNoticeSlug);
  const didFinishMobileInstructions =
    !hasMobileApplication || isSurveyAnswerTrue(devSurveyAnswers, DevInstructionsMobileNoticeSlug);

  const nonGppVendorsForConsentManagement = filterNonGppVendorsForConsentManagement(
    allVendors || [],
  );
  const showConsentManagementNotice = nonGppVendorsForConsentManagement.length > 0;
  const completedConsentManagementVendorIds = JSON.parse(
    devSurveyAnswers[DevInstructionsConsentManagementFinishedVendorIdsSlug] || "[]",
  );
  const didFinishConsentManagementInstructions =
    !showConsentManagementNotice ||
    nonGppVendorsForConsentManagement.every((v) =>
      completedConsentManagementVendorIds.includes(v.vendorId),
    );

  // Note: don't include `didFinishConsentManagementInstructions` since it is not a one-time onboarding step
  const allDevInstructionsNoticesFinished =
    didFinishInstoreInstructions &&
    didFinishWebInstructions &&
    didFinishCookieInstructions &&
    didFinishMobileInstructions;

  return {
    didFinishInstoreInstructions,
    didFinishWebInstructions,
    didFinishCookieInstructions,
    didFinishConsentManagementInstructions,
    didFinishMobileInstructions,
    allDevInstructionsNoticesFinished,
  };
};

export const PrivacyNoticeChecklistItems = ({
  notices,
  isHr = false,
  isPublicView = false,
}: PrivacyNoticeChecklistItemsProps) => {
  const [org] = usePrimaryOrganization();
  const {
    privacyCenter,
    isGoogleAnalytics,
    isSelling,
    isSharing,
    showCookieNotice,
    showFinancialIncentive,
    hasJobApplicants,
    hasPhysicalLocation,
    hasMobileApplication,
    employmentGroupNotices: employmentGroups,
  } = notices;

  const [allVendors, ,] = useOrganizationVendors(
    org?.id,
    /* excludeRecipientCategories = */ [],
    /* includeDataStorageLocations = */ false,
    /* excludeIncomplete = */ undefined,
    /* context = */ undefined,
    /* onlyUseSettled = */ true,
  );

  const empNotices = (employmentGroups && employmentGroups.length > 0) || hasJobApplicants;

  const websiteAudit = useWebsiteAudit();

  const orgId = usePrimaryOrganizationId();

  const [fullPrivacyCenter] = usePrivacyCenter(orgId);

  const {
    answers,
    refresh: refreshAnswers,
    setAnswer,
  } = useSurvey(orgId, DEVELOPER_INSTRUCTIONS_SURVEY_NAME);

  const {
    didFinishInstoreInstructions,
    didFinishWebInstructions,
    didFinishCookieInstructions,
    didFinishConsentManagementInstructions,
    didFinishMobileInstructions,
    allDevInstructionsNoticesFinished,
  } = getDevInstructionsCompletionStatus(answers, notices, allVendors);

  const showConsentManagementNotice =
    allVendors &&
    allVendors.length > 0 &&
    filterNonGppVendorsForConsentManagement(allVendors).length > 0;

  const didFinishHrNoticeInstructions =
    !empNotices || isSurveyAnswerTrue(answers, DevInstructionsHrNoticeSlug);

  useEffect(() => {
    const isMarkedComplete = !!answers[DevInstructionsSurveySlug];

    if (allDevInstructionsNoticesFinished && !isMarkedComplete) {
      setAnswer(DevInstructionsSurveySlug, "true");
    }
  }, [answers]);

  return (
    <div>
      <div className="tw-boundary">
        <p className="mt-8 font-medium text-neutral-800">
          {isHr === true
            ? "View the instructions below for publishing your HR notices"
            : "View the instructions below for publishing the notices on your site"}
        </p>
      </div>

      {!isPublicView && (
        <div
          style={{
            marginTop: "48px",
            borderRadius: "10px",
            boxShadow: "0px 2px 12px 0px rgba(0, 0, 0, 0.12)",
            padding: "24px",
            width: "945px",
          }}
        >
          <div style={{ fontSize: "18px", fontWeight: 500, color: "#332E42" }}>
            Have a developer? Share the instructions with them
          </div>
          <UrlNoticeAction
            url={frontendPathToUrl(
              RouteHelpers.instructions.id(
                isHr === true ? org?.hrInstructionsId : org?.devInstructionsId,
              ),
            )}
          />
        </div>
      )}

      {isHr && empNotices && (
        <>
          <Callout
            variant={CalloutVariant.LightPurpleBordered}
            collapsible
            collapsed={didFinishHrNoticeInstructions}
            title={"Employment Notices"}
            className="callout--centerAction"
            action={
              !isPublicView && (
                <InlineBadge
                  badgeType={didFinishHrNoticeInstructions ? "complete" : "finish-set-up"}
                />
              )
            }
            // @ts-ignore sx is a prop on all Material UI components, see https://mui.com/system/getting-started/the-sx-prop/
            sx={{
              width: "945px",
            }}
            style={{
              marginTop: "48px",
            }}
          >
            <EmploymentNotices
              privacyCenter={privacyCenter}
              employmentGroups={employmentGroups}
              hasJobApplicants={hasJobApplicants}
            />
          </Callout>

          {!didFinishHrNoticeInstructions && !isPublicView && (
            <MarkAsCompletedButton
              onClick={async () => {
                await setAnswer(DevInstructionsHrNoticeSlug, "true");
              }}
              width="905px"
            />
          )}
        </>
      )}

      {!isHr && (
        <Callout
          variant={CalloutVariant.LightPurpleBordered}
          collapsible
          collapsed={didFinishWebInstructions}
          title={"Web Notices"}
          id="web-notices-wrapper"
          className="callout--centerAction"
          action={
            !isPublicView && (
              <InlineBadge badgeType={didFinishWebInstructions ? "complete" : "finish-set-up"} />
            )
          }
          // @ts-ignore sx is a prop on all Material UI components, see https://mui.com/system/getting-started/the-sx-prop/
          sx={{
            width: "945px",
          }}
          style={{
            marginTop: "48px",
          }}
        >
          <WebInstructions
            isGoogleAnalytics={isGoogleAnalytics}
            isSelling={isSelling}
            isSharing={isSharing}
            canShowGANudge={didFinishWebInstructions}
            isGAMeasurementIdKnown={(privacyCenter.gaMeasurementId ?? "") !== ""}
            privacyCenter={privacyCenter}
            logoLinkUrl={fullPrivacyCenter?.logoLinkUrl ?? ""}
            showFinancialIncentive={showFinancialIncentive}
          />

          {!didFinishWebInstructions && !isPublicView && (
            <MarkAsCompletedButton
              onClick={async () => {
                await setAnswer(DevInstructionsWebNoticeSlug, "true");

                let nextSectionToScrollTo = showCookieNotice
                  ? "cookie"
                  : hasMobileApplication
                  ? "mobile"
                  : showConsentManagementNotice
                  ? "consent-management"
                  : hasPhysicalLocation
                  ? "in-store"
                  : null;
                if (nextSectionToScrollTo) {
                  setTimeout(() => {
                    document
                      .getElementById(`${nextSectionToScrollTo}-notices-wrapper`)
                      ?.scrollIntoView({
                        behavior: "smooth",
                        block: "start",
                      });
                  }, 1000);
                }
              }}
              width="905px"
            />
          )}
        </Callout>
      )}

      {showCookieNotice && !isHr && (
        <Callout
          variant={CalloutVariant.LightPurpleBordered}
          collapsible
          collapsed={didFinishCookieInstructions ? true : !didFinishWebInstructions}
          title={"Cookie Notices"}
          id="cookie-notices-wrapper"
          className="callout--centerAction"
          action={
            !isPublicView && (
              <InlineBadge badgeType={didFinishCookieInstructions ? "complete" : "finish-set-up"} />
            )
          }
          // @ts-ignore sx is a prop on all Material UI components, see https://mui.com/system/getting-started/the-sx-prop/
          sx={{
            width: "945px",
          }}
          style={{
            marginTop: "24px",
          }}
        >
          <CookieNotices
            cookies={notices.cookies}
            scanUrl={notices.cookieScanUrl ?? "your website"}
            hasShopify={notices.hasShopify}
          />

          {!didFinishCookieInstructions && !isPublicView && (
            <MarkAsCompletedButton
              onClick={async () => {
                await setAnswer(DevInstructionsCookieNoticeSlug, "true");

                let nextSectionToScrollTo = hasMobileApplication
                  ? "mobile"
                  : showConsentManagementNotice
                  ? "consent-management"
                  : hasPhysicalLocation
                  ? "in-store"
                  : null;
                if (nextSectionToScrollTo) {
                  setTimeout(() => {
                    document
                      .getElementById(`${nextSectionToScrollTo}-notices-wrapper`)
                      ?.scrollIntoView({
                        behavior: "smooth",
                        block: "start",
                      });
                  }, 1000);
                }
              }}
              width="905px"
            />
          )}
        </Callout>
      )}

      {hasMobileApplication && !isHr && (
        <Callout
          variant={CalloutVariant.LightPurpleBordered}
          collapsible
          collapsed={
            didFinishMobileInstructions
              ? true
              : !didFinishCookieInstructions || !didFinishWebInstructions
          }
          title={"Mobile Notices"}
          id="mobile-notices-wrapper"
          className="callout--centerAction"
          action={
            !isPublicView && (
              <InlineBadge badgeType={didFinishMobileInstructions ? "complete" : "finish-set-up"} />
            )
          }
          // @ts-ignore sx is a prop on all Material UI components, see https://mui.com/system/getting-started/the-sx-prop/
          sx={{
            width: "945px",
          }}
          style={{
            marginTop: "24px",
          }}
        >
          <MobileNotices
            privacyCenter={privacyCenter}
            isSelling={isSelling}
            isSharing={isSharing}
          />

          {!didFinishMobileInstructions && !isPublicView && (
            <MarkAsCompletedButton
              onClick={async () => {
                await setAnswer(DevInstructionsMobileNoticeSlug, "true");

                let nextSectionToScrollTo = showConsentManagementNotice
                  ? "consent-management"
                  : hasPhysicalLocation
                  ? "in-store"
                  : null;
                if (nextSectionToScrollTo) {
                  setTimeout(() => {
                    document
                      .getElementById(`${nextSectionToScrollTo}-notices-wrapper`)
                      ?.scrollIntoView({
                        behavior: "smooth",
                        block: "start",
                      });
                  }, 1000);
                }
              }}
              width="905px"
            />
          )}
        </Callout>
      )}

      {showConsentManagementNotice && !isHr && (
        <>
          <Callout
            variant={CalloutVariant.LightPurpleBordered}
            collapsible
            collapsed={
              didFinishConsentManagementInstructions
                ? true
                : !didFinishCookieInstructions ||
                  !didFinishWebInstructions ||
                  !didFinishMobileInstructions
            }
            title={"Consent Management"}
            id="consent-management-wrapper"
            className="callout--centerAction"
            action={
              !isPublicView && (
                <InlineBadge
                  badgeType={didFinishConsentManagementInstructions ? "complete" : "finish-set-up"}
                />
              )
            }
            // @ts-ignore sx is a prop on all Material UI components, see https://mui.com/system/getting-started/the-sx-prop/
            sx={{
              width: "945px",
            }}
            style={{
              marginTop: "24px",
            }}
          >
            <ConsentManagementNotices
              consentManagementNonGppVendors={filterNonGppVendorsForConsentManagement(allVendors)}
              devSurveyAnswers={answers}
              refreshDevSurveyAnswers={refreshAnswers}
              setDevSurveyAnswer={setAnswer}
            />

            {/* {!didFinishConsentManagementInstructions && !isPublicView && (
              <MarkAsCompletedButton
                onClick={async () => {
                  await setAnswer(DevInstructionsConsentManagementNoticeSlug, "true");

                  let nextSectionToScrollTo = hasPhysicalLocation ? "in-store" : null;
                  if (nextSectionToScrollTo) {
                    setTimeout(() => {
                      document
                        .getElementById(`${nextSectionToScrollTo}-notices-wrapper`)
                        ?.scrollIntoView({
                          behavior: "smooth",
                          block: "start",
                        });
                    }, 1000);
                  }
                }}
                width="905px"
              />
            )} */}
          </Callout>
        </>
      )}

      {hasPhysicalLocation && !isHr && (
        <>
          <div className="tw-boundary">
            <p className="font-medium text-neutral-800 mt-8">
              View the instructions below for publishing your notices in your store
            </p>
          </div>

          <Callout
            variant={CalloutVariant.LightPurpleBordered}
            collapsible
            collapsed={
              didFinishInstoreInstructions
                ? true
                : !didFinishWebInstructions ||
                  !didFinishMobileInstructions ||
                  !didFinishCookieInstructions
            }
            title={"In-Store Notices"}
            id="in-store-notices-wrapper"
            className="callout--centerAction"
            action={
              !isPublicView && (
                <InlineBadge
                  badgeType={didFinishInstoreInstructions ? "complete" : "finish-set-up"}
                />
              )
            }
            // @ts-ignore sx is a prop on all Material UI components, see https://mui.com/system/getting-started/the-sx-prop/
            sx={{
              width: "945px",
            }}
            style={{
              marginTop: "24px",
            }}
          >
            <InStoreNotices privacyCenter={privacyCenter} />

            {!didFinishInstoreInstructions && !isPublicView && (
              <MarkAsCompletedButton
                onClick={async () => {
                  await setAnswer(DevInstructionsInStoreNoticeSlug, "true");
                }}
                width="905px"
              />
            )}
          </Callout>
        </>
      )}

      {!isPublicView && !isHr && (
        <>
          {allDevInstructionsNoticesFinished && <CompletedButtonPlaceholder />}

          <div style={{ marginTop: "48px", color: "#332E42", fontSize: "18px", fontWeight: 900 }}>
            All done? We'll scan your site to ensure all required links are present
          </div>

          <WebsiteAudit
            useNewDesign={true}
            autoFocus={false}
            startScanDisabled={!allDevInstructionsNoticesFinished}
            {...websiteAudit.props}
          />
        </>
      )}
    </div>
  );
};

const InlineBadge = ({ badgeType }: { badgeType: "finish-set-up" | "complete" }) => {
  const bgColor = badgeType === "finish-set-up" ? "#FEDE8B" : "#82E3BC";
  const text = badgeType === "finish-set-up" ? "Finish Set Up" : "Complete";
  return (
    <div
      style={{
        backgroundColor: bgColor,
        color: "rgba(0, 0, 0, 0.87)",
        borderRadius: "100px",
        fontSize: "14px",
        fontWeight: "500",
        padding: "6px 12px",
      }}
    >
      {text}
    </div>
  );
};

export const MarkAsCompletedButton = ({
  onClick,
  width,
  disabled,
  label = "Mark as Completed",
}: {
  onClick: () => Promise<void>;
  width?: string;
  disabled?: boolean;
  label?: string;
}) => {
  return (
    <div
      style={{
        border: "1px solid #918CA1",
        borderRadius: "5px",
        padding: "10px 0",
        width: width || "945px",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        cursor: disabled ? "not-allowed" : "pointer",
        marginTop: "48px",
      }}
      onClick={!disabled ? onClick : undefined}
    >
      {!disabled && (
        <svg
          width="24"
          height="24"
          viewBox="0 0 24 24"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <mask
            id="mask0_5535_712"
            style={{ maskType: "alpha" }}
            maskUnits="userSpaceOnUse"
            x="0"
            y="0"
            width="24"
            height="24"
          >
            <rect width="24" height="24" fill="#D9D9D9" />
          </mask>
          <g mask="url(#mask0_5535_712)">
            <path
              d="M12 22C10.6167 22 9.31667 21.7375 8.1 21.2125C6.88333 20.6875 5.825 19.975 4.925 19.075C4.025 18.175 3.3125 17.1167 2.7875 15.9C2.2625 14.6833 2 13.3833 2 12C2 10.6167 2.2625 9.31667 2.7875 8.1C3.3125 6.88333 4.025 5.825 4.925 4.925C5.825 4.025 6.88333 3.3125 8.1 2.7875C9.31667 2.2625 10.6167 2 12 2C13.3833 2 14.6833 2.2625 15.9 2.7875C17.1167 3.3125 18.175 4.025 19.075 4.925C19.975 5.825 20.6875 6.88333 21.2125 8.1C21.7375 9.31667 22 10.6167 22 12C22 13.3833 21.7375 14.6833 21.2125 15.9C20.6875 17.1167 19.975 18.175 19.075 19.075C18.175 19.975 17.1167 20.6875 15.9 21.2125C14.6833 21.7375 13.3833 22 12 22ZM12 20C14.2333 20 16.125 19.225 17.675 17.675C19.225 16.125 20 14.2333 20 12C20 9.76667 19.225 7.875 17.675 6.325C16.125 4.775 14.2333 4 12 4C9.76667 4 7.875 4.775 6.325 6.325C4.775 7.875 4 9.76667 4 12C4 14.2333 4.775 16.125 6.325 17.675C7.875 19.225 9.76667 20 12 20Z"
              fill="#1C1B1F"
            />
          </g>
        </svg>
      )}
      <div
        style={{
          marginLeft: "10px",
          color: disabled ? "#918ca1" : "#1A1726",
          fontWeight: disabled ? undefined : 500,
        }}
      >
        {label}
      </div>
    </div>
  );
};

export const CompletedButtonPlaceholder = () => {
  return (
    <div
      style={{
        backgroundColor: "#F3F0FE",
        border: "1px solid #5331C6",
        borderRadius: "5px",
        padding: "10px 0",
        width: "945px",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        cursor: "pointer",
        marginTop: "48px",
      }}
    >
      <svg
        width="24"
        height="24"
        viewBox="0 0 24 24"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <mask
          id="mask0_5537_1623"
          style={{ maskType: "alpha" }}
          maskUnits="userSpaceOnUse"
          x="0"
          y="0"
          width="24"
          height="24"
        >
          <rect width="24" height="24" fill="#D9D9D9" />
        </mask>
        <g mask="url(#mask0_5537_1623)">
          <path
            d="M10.6 16.6L17.65 9.55L16.25 8.15L10.6 13.8L7.75 10.95L6.35 12.35L10.6 16.6ZM12 22C10.6167 22 9.31667 21.7375 8.1 21.2125C6.88333 20.6875 5.825 19.975 4.925 19.075C4.025 18.175 3.3125 17.1167 2.7875 15.9C2.2625 14.6833 2 13.3833 2 12C2 10.6167 2.2625 9.31667 2.7875 8.1C3.3125 6.88333 4.025 5.825 4.925 4.925C5.825 4.025 6.88333 3.3125 8.1 2.7875C9.31667 2.2625 10.6167 2 12 2C13.3833 2 14.6833 2.2625 15.9 2.7875C17.1167 3.3125 18.175 4.025 19.075 4.925C19.975 5.825 20.6875 6.88333 21.2125 8.1C21.7375 9.31667 22 10.6167 22 12C22 13.3833 21.7375 14.6833 21.2125 15.9C20.6875 17.1167 19.975 18.175 19.075 19.075C18.175 19.975 17.1167 20.6875 15.9 21.2125C14.6833 21.7375 13.3833 22 12 22ZM12 20C14.2333 20 16.125 19.225 17.675 17.675C19.225 16.125 20 14.2333 20 12C20 9.76667 19.225 7.875 17.675 6.325C16.125 4.775 14.2333 4 12 4C9.76667 4 7.875 4.775 6.325 6.325C4.775 7.875 4 9.76667 4 12C4 14.2333 4.775 16.125 6.325 17.675C7.875 19.225 9.76667 20 12 20Z"
            fill="#1C1B1F"
          />
        </g>
      </svg>
      <div style={{ marginLeft: "10px", color: "#130052", fontWeight: 500 }}>Completed</div>
    </div>
  );
};

type MobileNoticesProps = {
  privacyCenter: PublicPrivacyCenterDto;

  isSelling: boolean;
  isSharing: boolean;
};

const MobileNotices = ({ privacyCenter, isSelling, isSharing }: MobileNoticesProps) => {
  return (
    <div className="compliance-checklist--notices">
      <div className="compliance-checklist--notices-items">
        <ol className="custom p-0">
          <PrivacyNoticeTask
            description={
              <>
                On the mobile app’s download page (Apple App store, Google Play), update the link to
                your business’s Privacy Policy
              </>
            }
          >
            <UrlNoticeAction
              id="copy-privacy-policy-mobile-download"
              label="Privacy Policy"
              url={PrivacyCenterUrls.getPrivacyPolicyUrl(privacyCenter)}
            />
          </PrivacyNoticeTask>

          <PrivacyNoticeTask
            description={<>Update app description to include other privacy notices</>}
            helpText={
              <>Paste the text below into the app description on your app’s download page.</>
            }
          >
            <PrivacyNoticeActions
              content={
                <span className="text-t1 text-font-mono" style={{ whiteSpace: "pre-wrap" }}>
                  {getMobilePrivacyNoticesText(
                    PrivacyCenterUrls.getCAPrivacyNoticeUrl(privacyCenter),
                    privacyCenter,
                    isSelling,
                    isSharing,
                  )}
                </span>
              }
              actions={[
                getCopyAction(
                  "update-mobile-app-description-privacy-notices-copy-text",
                  "copy text",
                  getMobilePrivacyNoticesText(
                    PrivacyCenterUrls.getCAPrivacyNoticeUrl(privacyCenter),
                    privacyCenter,
                    isSelling,
                    isSharing,
                  ),
                ),
              ]}
            />
          </PrivacyNoticeTask>
          <PrivacyNoticeTask
            description={<>Within your business’s mobile app, post (or update) the links below</>}
            helpText={
              <>
                For example, add links to each notice in the app’s settings menu. Title the links
                using the exact language below.
              </>
            }
          >
            <UrlNoticeAction
              label="California Privacy Notice"
              url={PrivacyCenterUrls.getCAPrivacyNoticeUrl(privacyCenter)}
            />
            <UrlNoticeAction
              id="copy-privacy-policy-mobile-app"
              label="Privacy Policy"
              url={PrivacyCenterUrls.getPrivacyPolicyUrl(privacyCenter)}
            />

            {showOptOutNotice(isSelling, isSharing) && (
              <UrlNoticeAction
                url={PrivacyCenterUrls.getOptOutUrl(privacyCenter, isSelling, isSharing)}
                label={optOutLabel()}
              />
            )}
          </PrivacyNoticeTask>
          <PrivacyNoticeTask
            description={<>Implement consent management in your mobile app</>}
            helpText={
              <>
                Our cross-platform consent management API records a consumer's consent choices. Your
                app will query them before interfacing with 3rd party APIs or SDKs to ensure
                opt-outs are respected.
                <br />
                <br />
                See the implementation guide at{" "}
                <a
                  href="https://developers.truevault.com/docs/category/cross-platform-consent-management"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  developers.truevault.com/docs/category/cross-platform-consent-management
                </a>
              </>
            }
          />
        </ol>
      </div>
    </div>
  );
};

type CookieNoticeProps = {
  cookies: OrganizationCookieDto[];
  scanUrl?: string;
  hasShopify: boolean;
};
const CookieNotices = ({ cookies, scanUrl, hasShopify }: CookieNoticeProps) => {
  return (
    <div className="compliance-checklist--notices">
      <div className="compliance-checklist--notices-items">
        <ol className="custom p-0">
          <PrivacyNoticeTask
            description="Configure Cookie Consent"
            helpText={
              <Paras>
                <Para paragraph className="text-t1">
                  Certain privacy laws require consent for all website cookies beyond those that are
                  strictly necessary for the operation of your website. Below are a list of cookies
                  identified on <code>{scanUrl}</code> and the category they belong to. Follow the
                  instructions below to update your site such that each cookie listed does not load
                  unless the user has consented to the category of the cookie.
                </Para>
                <SurveyGateCollapse label="See cookies">
                  <DeveloperCookieTable cookies={cookies} />
                </SurveyGateCollapse>
              </Paras>
            }
          >
            <Paras>
              <NoticeSubHeading>Google Tag Manager</NoticeSubHeading>
              <Para paragraph className="text-t1">
                We strongly recommend using Google Tag Manager (GTM) to control consent requirements
                for third party tags that you load on your website. Follow{" "}
                <ExternalLink className="text-link" href={GTM_COOKIE_URL} target="_blank">
                  these instructions
                </ExternalLink>{" "}
                to integrate TrueVault’s consent management platform and configure the consent
                requirements for your tags in GTM.
              </Para>

              {hasShopify && (
                <>
                  <NoticeSubHeading>Shopify App Pixels</NoticeSubHeading>
                  <Para paragraph className="text-t1">
                    If any of your tags are Shopify App Pixels, our JavaScript will communicate a
                    consumer's consent preferences to Shopify by using the Shopify Customer Privacy
                    API. Shopify will then convey the consumer's consent preferences to App Pixels.
                    These Apps should behave according to the consent categories that the App
                    developer has specified. See our{" "}
                    <ExternalLink
                      className="text-link"
                      href={SHOPIFY_CONSENT_HELPSCOUT_URL}
                      target="_blank"
                    >
                      article on Shopify App Pixel consent
                    </ExternalLink>{" "}
                    for more information on how to validate consent setup for apps managed by
                    Shopify.
                  </Para>
                </>
              )}

              <NoticeSubHeading>Other Consent Management Options</NoticeSubHeading>
              <Para paragraph className="text-t1">
                Having trouble with the above setup, or have more complex needs? Let's get in touch:{" "}
                <ExternalLink
                  className="text-link"
                  href="mailto:help@truevault.com"
                  target="_blank"
                >
                  help@truevault.com
                </ExternalLink>
              </Para>
            </Paras>
          </PrivacyNoticeTask>

          {hasShopify && (
            <PrivacyNoticeTask
              description="Shopify Configuration"
              helpText={
                <Para paragraph className="text-t1">
                  Shopify stores should update their banner settings by following these steps:
                </Para>
              }
            >
              <Paras>
                <Para paragraph className="text-t1 mt-sm">
                  <strong>1. Enable Third-Party banner:</strong> Go to{" "}
                  <strong>Settings &gt; Customer Privacy &gt; Cookie banner &gt; Regions</strong>.
                  Click to enable the Regions where you use the TrueVault Banner. For example, you
                  can select <strong>Europe for GDPR</strong> and select any{" "}
                  <strong>US States</strong> where you have elected to use a consent banner.
                </Para>
                <Para paragraph className="text-t1">
                  <strong>2. Disable the Shopify banner:</strong> From your Shopify admin, go to{" "}
                  <strong>Settings &gt; Customer Privacy &gt; Cookie banner</strong>. Click{" "}
                  <strong>More actions</strong> and select <strong>Remove cookie banner</strong>.
                  Click <strong>Remove</strong>.
                </Para>
              </Paras>
            </PrivacyNoticeTask>
          )}

          <PrivacyNoticeTask
            description="Customize Cookie Consent Widget (Optional)"
            helpText={
              <Paras>
                <Para paragraph className="text-t1">
                  Our Cookie Consent Widget comes with a default set of styles out of the box. If
                  you’d like to customize the look and feel of the Cookie Consent Widget, you can do
                  so from the Website Consent section of your Privacy Center.
                </Para>
              </Paras>
            }
          />
        </ol>
      </div>
    </div>
  );
};

const ConsentManagementNotices = ({
  consentManagementNonGppVendors,
  devSurveyAnswers,
  refreshDevSurveyAnswers,
  setDevSurveyAnswer,
}: {
  consentManagementNonGppVendors: OrganizationDataRecipientDto[];
  devSurveyAnswers: SurveyAnswers;
  refreshDevSurveyAnswers: RefreshFn;
  setDevSurveyAnswer: SurveyAnswerFn;
}) => {
  const [isVendorsCollapsed, setIsVendorsCollapsed] = useState(false);

  const completedVendorIds = JSON.parse(
    devSurveyAnswers[DevInstructionsConsentManagementFinishedVendorIdsSlug] || "[]",
  );

  return (
    <div className="compliance-checklist--notices">
      <div className="compliance-checklist--notices-items">
        <div className="tw-boundary">
          <AlertCard
            variant="info"
            description={
              <>
                The following vendors are third-party selling or sharing vendors in your data map
                that either do not respect the IAB{" "}
                <a
                  href="https://iabtechlab.com/gpp"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-blue-600 underline"
                >
                  Global Privacy Platform (GPP)
                </a>{" "}
                or{" "}
                <a
                  href="https://globalprivacycontrol.org"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-blue-600 underline"
                >
                  Global Privacy Control (GPC)
                </a>{" "}
                signals, or their GPP/GPC support is unknown.
                <br />
                <br />
                To ensure opt-outs are respected for these vendors, you need to configure Google Tag
                Manager (GTM) to block their tags when a consumer has opted out. GTM provides the
                Consent Mode layer that enables scripts to be consent-controlled on your website.
              </>
            }
            containerClasses="mt-0"
          />
        </div>
        <ol className="custom p-0" style={{ marginTop: "16px" }}>
          <PrivacyNoticeTask
            description="Install the TrueVault CMP Template in Google Tag Manager"
            helpText={
              <Paras>
                <Para paragraph className="text-t1">
                  The TrueVault CMP Template communicates consent signals (including{" "}
                  <code>tv_not_opted_out</code>) to Google Tag Manager so that tags can be
                  automatically blocked for opted-out visitors. If you have not already set up the
                  template, follow the instructions at:{" "}
                  <a
                    href="https://developers.truevault.com/docs/google-tag-manager/setup-truevault-cmp-template"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    developers.truevault.com/docs/google-tag-manager/setup-truevault-cmp-template
                  </a>
                </Para>
              </Paras>
            }
          />
          <PrivacyNoticeTask
            description="Edit your tags to require the Non-Opt-Out Consent Signal"
            helpText={
              <Paras>
                <Para paragraph className="text-t1">
                  Review tags for the following vendors. If any of them should not run for opted-out
                  consumers, follow these steps:
                </Para>
              </Paras>
            }
          >
            <Paras>
              <ol className="custom custom-sublist p-0">
                <PrivacyNoticeTask description="In the Workspace tab of Google Tag Manager, select Tags from the left navigation bar" />
                <PrivacyNoticeTask description="Click on the tag you want to configure" />
                <PrivacyNoticeTask description="Click Tag Configuration" />
                <PrivacyNoticeTask description="Open Advanced Settings → Consent Settings" />
                <PrivacyNoticeTask description="Select Require additional consent for tag to fire, then Add required consent" />
                <PrivacyNoticeTask description="Set the tags to require:">
                  <PrivacyNoticeActions
                    content={
                      <span className="text-t1 text-font-mono" style={{ whiteSpace: "pre-wrap" }}>
                        {"tv_not_opted_out"}
                      </span>
                    }
                    actions={[
                      getCopyAction("copy-tv-not-opted-out", "copy text", "tv_not_opted_out"),
                    ]}
                  />
                </PrivacyNoticeTask>
                <PrivacyNoticeTask description="Click Save" />
                <PrivacyNoticeTask description="Once you've finished with all your tags and are ready for them to go live on your site, you'll need to publish the container. To do so, click Submit in the top-right corner, then Publish." />
              </ol>
            </Paras>
          </PrivacyNoticeTask>
        </ol>
      </div>

      <div className="tw-boundary">
        <div className="max-w-[300px] ml-[52px] mt-[32px]">
          <div
            className="flex items-center cursor-pointer"
            onClick={() => setIsVendorsCollapsed(!isVendorsCollapsed)}
          >
            <div className="text-[18px] font-medium text-secondary-600">See Vendors</div>
            <div
              className="ml-[12px]"
              style={isVendorsCollapsed ? { transform: "rotate(180deg)" } : {}}
            >
              <svg
                width="24"
                height="12"
                viewBox="0 0 24 12"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M22 1.775L12 11.775L2 1.775L3.775 0L12 8.225L20.225 0L22 1.775Z"
                  fill="#007BBD"
                />
              </svg>
            </div>
          </div>
          {!isVendorsCollapsed &&
            consentManagementNonGppVendors &&
            consentManagementNonGppVendors.length > 0 &&
            consentManagementNonGppVendors.map((vendor) => {
              return (
                <div
                  key={vendor.id}
                  className="flex gap-[12px] px-[24px] py-[14px] border-b border-solid border-gray-200"
                >
                  <div>
                    <SimpleCheckboxControlled
                      name={`consent-management-${vendor.id}`}
                      checked={completedVendorIds.includes(vendor.vendorId)}
                      onChange={async () => {
                        const newCompletedVendorIds = completedVendorIds.includes(vendor.vendorId)
                          ? completedVendorIds.filter((v) => v !== vendor.vendorId)
                          : [...completedVendorIds, vendor.vendorId];
                        await setDevSurveyAnswer(
                          DevInstructionsConsentManagementFinishedVendorIdsSlug,
                          JSON.stringify(newCompletedVendorIds),
                        );
                        await refreshDevSurveyAnswers();
                      }}
                    />
                  </div>
                  <img
                    src={vendor.logoUrl}
                    alt={vendor.name}
                    style={{ width: "24px", height: "24px" }}
                  />
                  <div>{vendor.name}</div>
                </div>
              );
            })}
        </div>
      </div>

      <NoticeSubHeading>Other Consent Management Options</NoticeSubHeading>
      <Para paragraph className="text-t1">
        Having trouble with the above setup, or have more complex needs? Let's get in touch:{" "}
        <ExternalLink className="text-link" href="mailto:help@truevault.com" target="_blank">
          help@truevault.com
        </ExternalLink>
      </Para>
    </div>
  );
};

type InStoreNoticesProps = {
  privacyCenter: PublicPrivacyCenterDto;
};

const InStoreNotices = ({ privacyCenter }: InStoreNoticesProps) => {
  return (
    <div className="compliance-checklist--notices">
      <div className="compliance-checklist--notices-items">
        <ol className="custom p-0">
          <PrivacyNoticeTask
            description="Post California Privacy Notice at consumer-facing stores or offices"
            helpText={
              <>
                Post prominent signage directing consumers to the URL where your business's online
                California Privacy Notice can be found.
              </>
            }
          >
            <UrlNoticeAction
              url={PrivacyCenterUrls.getCAPrivacyNoticeUrl(privacyCenter)}
              label="California Privacy Notice"
            />
          </PrivacyNoticeTask>
          <PrivacyNoticeTask
            description="Add California Privacy Notice to any other offline points of collection"
            helpText={
              <>
                Add the California Privacy Notice URL or the actual text of the notice to all other
                offline places where your business collects information from consumers in
                California, such as on paper forms.
              </>
            }
          >
            <UrlNoticeAction
              url={PrivacyCenterUrls.getCAPrivacyNoticeUrl(privacyCenter)}
              label="California Privacy Notice"
            />
          </PrivacyNoticeTask>
        </ol>
      </div>
    </div>
  );
};

type EmploymentNoticesProps = {
  privacyCenter: PublicPrivacyCenterDto;

  employmentGroups: EmploymentGroupNotices[];
  hasJobApplicants: boolean;
};
const EmploymentNotices = ({
  privacyCenter,
  employmentGroups,
  hasJobApplicants,
}: EmploymentNoticesProps) => {
  return (
    <>
      <div className="compliance-checklist--notices">
        <div className="compliance-checklist--notices-items">
          <ol className="custom p-0">
            {employmentGroups.length > 0 && (
              <PrivacyNoticeTask
                description="Post each Employment Notice at point of collection"
                helpText={
                  <>
                    Include each notice at or before the point where your business collects personal
                    information from California employees, such as accompanying an offer letter;
                    included with onboarding materials; as an addendum or enclosure with a contract
                    (e.g., for contractors); or in an employee handbook.
                  </>
                }
              >
                {employmentGroups
                  .sort((a, b) => a.name.localeCompare(b.name))
                  .map((cg) => {
                    return (
                      <UrlNoticeAction key={cg.name} url={cg.url} label={`Notice to ${cg.name}`} />
                    );
                  })}
              </PrivacyNoticeTask>
            )}
            {hasJobApplicants && (
              <PrivacyNoticeTask
                description="Link to Job Applicant Notice at point of collection"
                helpText={
                  <>
                    Add a "Notice to California Job Applicants" link where your business collects
                    personal information from job applicants in California, such as where it posts
                    hiring ads online.
                  </>
                }
              >
                <UrlNoticeAction
                  url={PrivacyCenterUrls.getJobApplicantsNoticeUrl(privacyCenter)}
                  label="Notice to California Job Applicants"
                />
              </PrivacyNoticeTask>
            )}
          </ol>
        </div>
      </div>
    </>
  );
};

type WebInstructionsProps = {
  privacyCenter: PublicPrivacyCenterDto;
  logoLinkUrl: string;
  isSelling: boolean;
  isSharing: boolean;
  isGoogleAnalytics: boolean;
  isGAMeasurementIdKnown: boolean;
  showFinancialIncentive: boolean;
  canShowGANudge: boolean;
};

const GTM_COOKIE_URL: string =
  "https://developers.truevault.com/docs/google-tag-manager/configure-cookie-consent";

const SHOPIFY_CONSENT_HELPSCOUT_URL: string =
  "https://developers.truevault.com/docs/shopify/shopify-app-pixel-consent";

const WebInstructions = ({
  privacyCenter,
  logoLinkUrl,
  isSelling,
  isSharing,
  isGoogleAnalytics,
  isGAMeasurementIdKnown,
  showFinancialIncentive,
  canShowGANudge,
}: WebInstructionsProps) => {
  const polarisJsHtml = getPolarisHtml(privacyCenter.id);

  return (
    <div className="compliance-checklist--notices">
      <div className="compliance-checklist--notices-items">
        <ol className="custom p-0">
          <PrivacyNoticeTask
            description="Update all Privacy Policy links on your website"
            helpText="Edit all Privacy Policy links on your business's website so that they link to the new Privacy Policy."
          >
            <UrlNoticeAction
              id="copy-privacy-policy-web"
              label="Privacy Policy"
              url={PrivacyCenterUrls.getPrivacyPolicyUrl(privacyCenter)}
            />
          </PrivacyNoticeTask>
          <PrivacyNoticeTask
            description="Add Polaris Client-Side JavaScript to your website"
            helpText={
              <>
                <div className="text-t1">
                  Add the following JavaScript in the top of the <code>&#60;head&#62;</code> element
                  of your business's website. It must be the{" "}
                  <strong>first functional script</strong> (important: if you use Google Tag
                  Manager, place the Polaris script above the GTM script).
                </div>

                <div className="text-t1 mt-lg">
                  This script is required for Polaris to function properly. It performs several
                  critical functions including limiting the visibility of state-specific links and
                  notices on your business's website so that visitors from states with privacy laws
                  will see the notices that apply to them.
                </div>

                {isGoogleAnalytics && (
                  <>
                    {!isGAMeasurementIdKnown && canShowGANudge && (
                      <div className="text-t1 mt-lg">
                        <p>
                          <Callout variant={CalloutVariant.Yellow} icon={InfoIcon}>
                            <span>
                              Your data map indicates that you are using Google Analytics. To allow
                              the Google Analytics opt-out integration to work on your website, you
                              need to add your Google Analytics Measurement ID on the
                            </span>{" "}
                            <Link to={Routes.privacyCenter.integrations}>Integrations</Link>{" "}
                            <span>page</span>.
                          </Callout>
                        </p>
                      </div>
                    )}
                    <div className="text-t1 mt-sm">
                      <strong>Note:</strong> To ensure that Polaris can evaluate GA opt-outs prior
                      to the Google Analytics script getting executed, do not add the "defer" or
                      "async" attributes to the <strong>polaris.js</strong> script.
                    </div>
                  </>
                )}
              </>
            }
          >
            <PrivacyNoticeActions
              content={
                <span className="text-t1 text-font-mono text-pre-wrap">{polarisJsHtml}</span>
              }
              actions={[
                getCopyAction("add-dns-link-to-website-script", "copy HTML", polarisJsHtml),
              ]}
            />
          </PrivacyNoticeTask>
          <PrivacyNoticeTask
            description="Add California Privacy Notice to website footer"
            helpText={
              <>
                Place the script below where you'd like the link to the{" "}
                <ExternalLink
                  className="text-link text-medium"
                  href={PrivacyCenterUrls.getCAPrivacyNoticeUrl(privacyCenter)}
                  target="_blank"
                >
                  California Privacy Notice
                </ExternalLink>{" "}
                to appear. The link is typically placed in the website footer near the Privacy
                Policy link.
              </>
            }
          >
            <PrivacyNoticeActions
              content={
                <span className="text-t1 text-font-mono">
                  {getCaPrivacyNoticeHtml(PrivacyCenterUrls.getCAPrivacyNoticeUrl(privacyCenter))}
                </span>
              }
              actions={[
                getCopyAction(
                  "add-ca-privacy-notice-website-home-script",
                  "copy HTML",
                  getCaPrivacyNoticeHtml(PrivacyCenterUrls.getCAPrivacyNoticeUrl(privacyCenter)),
                ),
              ]}
            />
          </PrivacyNoticeTask>
          <PrivacyNoticeTask
            description="Add California Privacy Notice to any other points of collection on website"
            helpText={
              <>
                Points on a website where a{" "}
                <ExternalLink
                  className="text-link text-medium"
                  href={PrivacyCenterUrls.getCAPrivacyNoticeUrl(privacyCenter)}
                  target="_blank"
                >
                  California Privacy Notice
                </ExternalLink>{" "}
                link should be added include pages where people can sign up for emails, create an
                account, make a purchase (checkout), or fill out a form.
              </>
            }
          >
            <PrivacyNoticeActions
              content={
                <span className="text-t1 text-font-mono">
                  {getCaPrivacyNoticeHtml(PrivacyCenterUrls.getCAPrivacyNoticeUrl(privacyCenter))}
                </span>
              }
              actions={[
                getCopyAction(
                  "add-ca-privacy-notice-website-collection-script",
                  "copy HTML",
                  getCaPrivacyNoticeHtml(PrivacyCenterUrls.getCAPrivacyNoticeUrl(privacyCenter)),
                ),
              ]}
            />
          </PrivacyNoticeTask>

          {showFinancialIncentive && (
            <PrivacyNoticeTask
              description="Add Notice of Financial Incentive to opt-in points"
              helpText={
                "Post the Notice of Financial Incentive link at all points on your website where a consumer can opt-in to the financial incentive or price or service difference described in the Notice."
              }
            >
              <PrivacyNoticeActions
                content={
                  <span className="text-t1 text-mono">
                    {getIncentiveNoticeHtml(privacyCenter.url)}
                  </span>
                }
                actions={[
                  getCopyAction(
                    "add-ca-privacy-notice-website-collection-script",
                    "copy HTML",
                    getIncentiveNoticeHtml(privacyCenter.url),
                  ),
                ]}
              />
            </PrivacyNoticeTask>
          )}
          <SellingOrSharingOptOut
            privacyCenter={privacyCenter}
            isSelling={isSelling}
            isSharing={isSharing}
          />
          <PrivacyNoticeTask
            description="Verify Privacy Policy link displays on a mobile device"
            helpText={
              "Visit your business's website on a mobile device and check that the Privacy " +
              "Policy link is visible."
            }
          />
          <PrivacyNoticeTask
            description="(Optional) Add footer link to cookie pop-up"
            helpText={
              <>
                You can optionally add the link text "Cookie Preferences" to your website footer —
                for example, below the "Your Privacy Choices" link — using the following URL:
              </>
            }
          >
            <PrivacyNoticeActions
              content={
                <span className="text-t1 text-font-mono">
                  {getCookiePreferencesHtml(logoLinkUrl)}
                </span>
              }
              actions={[
                getCopyAction(
                  "add-cookie-preferences-link-script",
                  "copy HTML",
                  getCookiePreferencesHtml(logoLinkUrl),
                ),
              ]}
            />
          </PrivacyNoticeTask>
        </ol>
      </div>
    </div>
  );
};

const showOptOutNotice = (isSelling: boolean, isSharing: boolean) => isSelling || isSharing;

const optOutLabel = () => "Your Privacy Choices";

const SellingOrSharingOptOut = ({
  privacyCenter,
  isSelling,
  isSharing,
}: {
  privacyCenter: PublicPrivacyCenterDto;
  isSelling: boolean;
  isSharing: boolean;
}) => {
  const [html, setHtml] = useState(null);

  if (!isSelling && !isSharing) {
    return null;
  }

  const linkCopy = optOutLabel();
  const link = PrivacyCenterUrls.getOptOutUrl(privacyCenter, isSelling, isSharing);

  return (
    <PrivacyNoticeTask
      description="Add Opt-Out Notice to website footer"
      helpText={
        <>
          Place the link below where you’d like the{" "}
          <ExternalLink className="text-link text-medium" href={link} target="_blank">
            {linkCopy}
          </ExternalLink>{" "}
          link to appear. The link is typically placed in the footer near the Privacy Policy link.
          You may change the style of the link, but do not change the link text. The link will be
          visible only to California web visitors.
        </>
      }
    >
      <OptOutIconSelector linkUrl={link} linkCopy={linkCopy} onUpdate={setHtml} />
      <PrivacyNoticeActions
        content={<span className="text-t1 text-font-mono text-pre-wrap">{html}</span>}
        actions={[getCopyAction("add-opt-out-notice-website-home-script", "copy HTML", html)]}
      />
    </PrivacyNoticeTask>
  );
};

const POLARIS_JS_HTML_START = "<!-- Start of TrueVault Code Block -->\n";

const getPolarisJSHTMLEnd = (privacyCenterId: string): string => {
  return (
    `<script src="https://polaris.truevaultcdn.com/static/pc/${privacyCenterId}/polaris.js">\n</script>\n` +
    `<!-- End of TrueVault Code Block -->`
  );
};

const getPolarisHtml = (privacyCenterId: string): string => {
  return POLARIS_JS_HTML_START + getPolarisJSHTMLEnd(privacyCenterId);
};

const getCaPrivacyNoticeHtml = (url: string) =>
  `<a class="truevault-polaris-privacy-notice" href="${url}" noreferrer noopener hidden>California Privacy Notice</a>`;

const getCookiePreferencesHtml = (url: string) =>
  `<a href="https://${url}?polaris.openConsentManager=true">Cookie Preferences</a>`;

export const getIncentiveNoticeHtml = (url: string) =>
  `<a class="truevault-polaris-privacy-notice" href="${url}/privacy-policy#financial-incentive" noreferrer noopener hidden>Notice of Financial Incentive</a>`;

const getMobilePrivacyNoticesText = (
  caPrivacyNoticeUrl: string,
  privacyCenter: PublicPrivacyCenterDto,
  isSelling: boolean,
  isSharing: boolean,
) => {
  const caNotice = `California Privacy Notice:\n${caPrivacyNoticeUrl}\n\n`;

  const showOptOut = showOptOutNotice(isSelling, isSharing);
  const label = optOutLabel();
  const link = PrivacyCenterUrls.getOptOutUrl(privacyCenter, isSelling, isSharing);
  const dnsNotice = showOptOut ? `${label}:\n${link}\n\n` : "";
  return `${caNotice}${dnsNotice}See our Privacy Policy to access ${
    showOptOut ? "these links" : "this link"
  }.`;
};

export type PrivacyNoticeChecklistNoticeItemAction = {
  id: string;
  content: React.ReactNode;
  onClick: () => void;
};

export const getCopyAction = (
  id: string | undefined,
  text: string,
  copyText: string,
): PrivacyNoticeChecklistNoticeItemAction => {
  return {
    id,
    content: (
      <>
        <span className="compliance-checklist--copy-done">
          <Done />{" "}
        </span>
        <span className="compliance-checklist--copy-copy">
          <FileCopyOutlined />
        </span>
        {text}
      </>
    ),
    onClick: () => {
      navigator.clipboard.writeText(copyText);
      document.getElementById(id)?.classList.add("copy-done");
      setTimeout(() => document.getElementById(id)?.classList.remove("copy-done"), 2000);
    },
  };
};

const UrlNoticeAction = ({ url, label, id }: { url: string; label?: string; id?: string }) => {
  const actions = [];

  actions.push(getCopyAction(id ?? `copy-${url}`, "copy URL", url));

  return (
    <PrivacyNoticeActions
      content={<span className="text-t1 text-mono">{label || url}</span>}
      actions={actions}
    />
  );
};

interface PrivacyNoticeTaskProps {
  description: string | React.ReactNode;
  helpText?: string | React.ReactNode;
  children?: React.ReactNode | React.ReactNodeArray;
}
const PrivacyNoticeTask = ({ description, helpText, children }: PrivacyNoticeTaskProps) => {
  return (
    <li className="compliance-checklist--notices-item">
      <div className="compliance-checklist--notices-item-content">
        <div className="compliance-checklist--notices-item-description">{description}</div>
        <div className="compliance-checklist--notices-item-help-text">{helpText}</div>
        {children && <div className="compliance-checklist--notices-item-actions">{children}</div>}
      </div>
    </li>
  );
};

const NoticeSubHeading = ({ children }: { children?: React.ReactNode }) => (
  <h4 className={`text-t1 text-spacing-spread text-muted text-weight-regular`}>{children}</h4>
);
